import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Zap, Heart, Phone, Mail, Sparkles, TrendingUp, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ServiceCard } from '../components/ServiceCard';
import { AnimateOnScroll } from '../components/AnimateOnScroll';
import { PhilosophieSection } from '../components/PhilosophieSection';
import { trackEvent } from '../components/GoogleAnalytics';
import { servicesData, methodSteps } from '../data/mock';

const BOOKING_URL = "https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled";

export const Home = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-orange-50 to-cyan-50 -z-10" />
        
        {/* Formes décoratives animées */}
        <div className="absolute top-20 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-orange-400/20 to-pink-400/20 rounded-full blur-3xl animate-float delay-500" />
        
        <div className="mx-auto max-w-7xl relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge animé */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6 animate-fade-in shadow-lg">
              <Sparkles className="w-4 h-4" />
              <span>Consultant IT Indépendant • Lyon & Région</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-fade-in-up">
              Confiez votre informatique à un expert,{' '}
              <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
                concentrez‑vous sur votre métier.
              </span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 leading-relaxed animate-fade-in-up delay-200 max-w-3xl mx-auto">
              Votre infrastructure mérite d'être chouchoutée : moins de pannes, plus de sérénité, avec un interlocuteur unique qui connaît vraiment votre entreprise.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fade-in-up delay-300">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                data-testid="hero-booking-btn"
              >
                <a 
                  href={BOOKING_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('booking_click', { event_category: 'Conversion', event_label: 'Hero CTA' })}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Prendre RDV gratuit (30 min)
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-300 transform hover:scale-105 shadow-lg"
                data-testid="hero-contact-btn"
              >
                <Link to="/contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Décrire mon besoin
                </Link>
              </Button>
            </div>

            {/* Stats rapides */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-16 animate-fade-in delay-500">
              {[
                { icon: Users, label: 'TPE & PME', value: 'Tous secteurs' },
                { icon: Clock, label: 'Réactivité', value: 'Remote first' },
                { icon: Shield, label: 'Sécurité', value: 'Prioritaire' },
                { icon: Heart, label: 'Approche', value: 'Humaine' }
              ].map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <stat.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* À qui je m'adresse */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white relative">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-orange-200/30 to-pink-200/30 rounded-full blur-3xl" />
        <div className="mx-auto max-w-7xl relative z-10">
          <AnimateOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
                Pour qui ?
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                À qui je m'adresse ?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                J'accompagne les <strong className="text-orange-600">entrepreneurs, TPE et PME</strong> de la région lyonnaise, tous secteurs confondus. 
                J'ai une expérience particulière dans l'hôtellerie, la restauration rapide, le service public et l'industrie, 
                mais je m'adapte à tout environnement professionnel ayant besoin d'une informatique fiable et d'un 
                accompagnement sur mesure.
              </p>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Services principaux */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-cyan-300/20 to-blue-300/20 rounded-full blur-3xl" />
        
        <div className="mx-auto max-w-7xl relative z-10">
          <AnimateOnScroll>
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
                Solutions complètes
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Mes services
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Des solutions sur mesure pour tous vos besoins informatiques
              </p>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {servicesData.map((service, index) => (
              <AnimateOnScroll key={service.id} delay={index * 100}>
                <ServiceCard service={service} />
              </AnimateOnScroll>
            ))}
          </div>
          <AnimateOnScroll delay={200}>
            <div className="text-center mt-10 sm:mt-12">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg transform hover:scale-105 transition-all">
                <Link to="/services">
                  Voir tous les services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* Pourquoi travailler avec moi */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        <div className="absolute top-10 left-0 w-64 sm:w-80 h-64 sm:h-80 bg-gradient-to-br from-orange-300/20 to-pink-300/20 rounded-full blur-3xl" />
        
        <div className="mx-auto max-w-7xl relative z-10">
          <AnimateOnScroll>
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block px-4 py-1 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
                Ma différence
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi travailler avec moi ?
              </h2>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                icon: Users,
                title: 'Proximité et interlocuteur unique',
                description: 'Une relation de confiance avec un consultant qui connaît votre entreprise et vos enjeux.',
                gradient: 'from-blue-500 to-cyan-500',
                bg: 'from-blue-50 to-cyan-50'
              },
              {
                icon: Target,
                title: 'Solutions sur mesure',
                description: 'Pas de packs génériques : chaque solution est adaptée à votre contexte et vos besoins réels.',
                gradient: 'from-orange-500 to-pink-500',
                bg: 'from-orange-50 to-pink-50'
              },
              {
                icon: Heart,
                title: 'Approche pédagogique',
                description: 'Je vulgarise et explique pour que vous compreniez ce qui est fait, sans jargon inutile.',
                gradient: 'from-pink-500 to-rose-500',
                bg: 'from-pink-50 to-rose-50'
              },
              {
                icon: Zap,
                title: 'Réactivité',
                description: 'Priorité au remote pour aller vite, avec possibilité de déplacement si nécessaire.',
                gradient: 'from-cyan-500 to-blue-500',
                bg: 'from-cyan-50 to-blue-50'
              }
            ].map((item, index) => (
              <AnimateOnScroll key={index} delay={index * 100}>
                <div className="text-center group">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-xl`}>
                    <item.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <div className={`bg-gradient-to-br ${item.bg} rounded-2xl p-5 sm:p-6 h-full transform group-hover:shadow-xl transition-all duration-300`}>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Ma philosophie / Mon engagement */}
      <PhilosophieSection variant="full" />

      {/* Méthode en 4 étapes */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-blue-900 to-cyan-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-56 sm:w-80 h-56 sm:h-80 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl" />
        
        <div className="mx-auto max-w-7xl relative z-10">
          <AnimateOnScroll>
            <div className="text-center mb-10 sm:mb-12">
              <span className="inline-block px-4 py-1 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-semibold mb-4">
                Processus simple et clair
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Ma méthode de travail
              </h2>
              <p className="text-base sm:text-lg text-blue-100 max-w-2xl mx-auto">
                Un processus clair et structuré pour votre tranquillité d'esprit
              </p>
            </div>
          </AnimateOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {methodSteps.map((step, index) => (
              <AnimateOnScroll key={step.number} delay={index * 100}>
                <div className="relative group h-full">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 h-full border border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 text-white flex items-center justify-center text-xl sm:text-2xl font-bold mb-6 shadow-lg transform group-hover:scale-110 transition-transform">
                      {step.number}
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-cyan-300 mb-4 font-medium">{step.duration}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
          <AnimateOnScroll delay={200}>
            <div className="text-center mt-10 sm:mt-12">
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 transition-all transform hover:scale-105">
                <Link to="/methode">
                  Découvrir ma méthode en détail
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 via-pink-500 to-orange-600 relative overflow-hidden">
        <div className="mx-auto max-w-4xl text-center relative z-10">
          <AnimateOnScroll>
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-6">
              <TrendingUp className="w-4 h-4" />
              <span>Commençons votre transformation digitale</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à stabiliser votre informatique ?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-8">
              Parlons de vos besoins lors d'un échange de 30 minutes, sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-100 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <a 
                  href={BOOKING_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('booking_click', { event_category: 'Conversion', event_label: 'Footer CTA' })}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Réserver un appel de 30 minutes
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-600 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all duration-300 transform hover:scale-105"
              >
                <Link to="/contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Envoyer une demande
                </Link>
              </Button>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </div>
  );
};
